import * as React from 'react'

import { Modal } from '@thg-commerce/gravity-elements'
import { HorizontalAlignment, i18n } from '@thg-commerce/enterprise-core'
import { SocialEmbed } from '@thg-commerce/enterprise-widget-social-embed'

import {
  Container,
  Title,
  Subtitle,
  VideoGalleryGridItem,
  ModalTitle,
  StyledGrid,
} from './styles'
import { VideoGalleryItem, VideoGalleryItemProps } from './VideoGalleryItem'

export interface VideoGalleryProps {
  title?: string
  subtitle?: string
  items: VideoGalleryItemProps[]
  itemsContentAlignment?: HorizontalAlignment
  titleAlignment?: HorizontalAlignment
  useTitleHeadingLevel2?: boolean
  numberOfColumns?: number
  className?: string
}

const getColSpan = (itemsNo) => {
  switch (itemsNo) {
    case 1:
      return [12, 8, 6, 6]
    case 2:
      return [12, 6, 6, 6]
    default:
      return [12, 6, 4, 4]
  }
}

export const VideoGallery = ({
  title,
  subtitle,
  items,
  itemsContentAlignment,
  titleAlignment,
  useTitleHeadingLevel2,
  numberOfColumns,
  className,
}: VideoGalleryProps) => {
  const [openVideo, setOpenVideo] = React.useState(false)
  const [displayVideoModal, setDisplayVideoModal] = React.useState(false)
  const [videoContent, setVideoContent] = React.useState<{
    title?: string
    videoUrl?: string
    id?: number
  }>({})
  const [width, setWidth] = React.useState(0)

  const i18nText = {
    closeAriaLabel: i18n('general.modal.close.button.arialabel'),
    closeLabel: i18n('general.modal.close.button.label'),
  }

  const updateWidth = () => {
    if (typeof Window !== 'undefined') {
      setWidth(window.innerWidth)
    }
  }

  React.useEffect(() => {
    setDisplayVideoModal(
      !(width < 600 || items.length === 1 || numberOfColumns === 1),
    )
  }, [width, numberOfColumns])

  React.useEffect(() => {
    if (typeof Window !== 'undefined') {
      window.addEventListener('resize', updateWidth)
    }
    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [typeof Window !== 'undefined' && window.innerWidth])

  React.useEffect(() => {
    updateWidth()
  }, [])

  const videoGalleryItems = items.map((item, index) => {
    return (
      <VideoGalleryGridItem
        key={index}
        colSpan={getColSpan(numberOfColumns || items.length)}
        colStart={
          items.length === 1 || numberOfColumns === 1 ? [1, 3, 4, 4] : undefined
        }
      >
        <VideoGalleryItem
          {...item}
          playVideo={(itemContent) => {
            setOpenVideo(true)
            setVideoContent(itemContent)
          }}
          displayVideoOnMobile={
            !displayVideoModal && openVideo && videoContent.id === index
          }
          itemId={index}
          windowWidth={width}
          isOneColumn={numberOfColumns === 1}
        />
      </VideoGalleryGridItem>
    )
  })

  return (
    <Container className={className}>
      {title && (
        <Title
          as={useTitleHeadingLevel2 ? 'h2' : 'div'}
          alignment={titleAlignment}
          data-testid="video-gallery-title"
        >
          {title}
        </Title>
      )}
      {subtitle && <Subtitle alignment={titleAlignment}>{subtitle}</Subtitle>}
      <StyledGrid
        alignment={itemsContentAlignment}
        data-testid="video-gallery-grid-component"
      >
        {videoGalleryItems}
      </StyledGrid>

      {displayVideoModal && videoContent.videoUrl && (
        <Modal
          i18nText={i18nText}
          open={openVideo}
          gridColSpan={[12, 8, 8, 6]}
          onClose={() => setOpenVideo(false)}
          verticalAlignment="center"
          showHeader={true}
          stickyHeader={true}
          data-testid="video-gallery-video-modal"
        >
          {videoContent.title && <ModalTitle>{videoContent.title}</ModalTitle>}
          <SocialEmbed
            provider="youtube"
            url={videoContent.videoUrl}
            isAmp={false}
          />
        </Modal>
      )}
    </Container>
  )
}
